import React, { useState } from "react";
import {
  Sparkles,
  Plus,
  Lock,
  Key,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Cpu,
  Trash2,
  Edit2,
  Sliders,
  HelpCircle,
  ExternalLink,
} from "lucide-react";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import LLMTestConfirmModal from "./LLMTestConfirmModal";

export const STATUS_CONFIG = {
  not_configured: {
    label: "Sin credenciales",
    badgeClass: "bg-[#222] text-[#888] border-[#333]",
    icon: Lock,
  },
  configured: {
    label: "Clave cifrada en bóveda",
    badgeClass: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    icon: Key,
  },
  credentials_verified: {
    label: "Credenciales verificadas",
    badgeClass: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    icon: CheckCircle2,
  },
  inference_verified: {
    label: "Inferencia verificada",
    badgeClass: "bg-amber-500/15 text-[#f5b544] border-amber-500/30",
    icon: Sparkles,
  },
  error: {
    label: "Error de conexión",
    badgeClass: "bg-red-500/10 text-red-400 border-red-500/20",
    icon: XCircle,
  },
};

export const LLMProvidersList = ({
  providers = [],
  loading = false,
  onRefresh,
  onOpenCreate,
  onOpenEdit,
  onDelete,
  onTestCredentials,
  onTestInference,
  testingProviderId = null,
}) => {
  const [inferenceModalTarget, setInferenceModalTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [feedbackNotice, setFeedbackNotice] = useState(null);

  const handleRunCredentialsTest = async (prov) => {
    setFeedbackNotice(null);
    try {
      const res = await onTestCredentials(prov.id);
      setFeedbackNotice({
        type: res.success ? "success" : "info",
        message: res.message,
        detail: res.error_detail,
      });
    } catch (err) {
      setFeedbackNotice({
        type: "error",
        message: "Error ejecutando la comprobación de credenciales.",
        detail: err?.message,
      });
    }
  };

  const handleConfirmInferenceTest = async (payload) => {
    if (!inferenceModalTarget) return;
    const targetId = inferenceModalTarget.id;
    try {
      const res = await onTestInference(targetId, payload);
      setFeedbackNotice({
        type: res.success ? "success" : "error",
        message: res.message,
        detail: res.error_detail,
      });
      setInferenceModalTarget(null);
    } catch (err) {
      setFeedbackNotice({
        type: "error",
        message: "Error ejecutando la prueba de inferencia.",
        detail: err?.message,
      });
      setInferenceModalTarget(null);
    }
  };

  return (
    <div data-testid="llm-providers-list" className="flex flex-col gap-6">
      {/* Banner de segregación de dominios */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-[#f5b544]/20 bg-[#f5b544]/5 p-4 text-xs">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#f5b544]/30 bg-[#f5b544]/10 text-[#f5b544]">
            <Sparkles size={16} />
          </div>
          <div>
            <div className="font-semibold text-[#ededed]">
              Conexiones del Creative Director IA
            </div>
            <p className="text-[11px] text-[#a3a3a3]">
              Infraestructura dedicada a razonamiento cinematográfico y análisis de escena. Completamente segregada de APIMart y de los costes de generación audiovisual.
            </p>
          </div>
        </div>

        <button
          type="button"
          data-testid="llm-new-provider-btn"
          onClick={onOpenCreate}
          className="v-btn-primary v-btn-sm shrink-0 inline-flex items-center gap-1.5"
        >
          <Plus size={14} />
          <span>Añadir Conexión LLM</span>
        </button>
      </div>

      {/* Notificación de feedback de pruebas */}
      {feedbackNotice && (
        <div
          data-testid="llm-test-feedback-notice"
          className={`flex items-start justify-between gap-3 rounded-xl border p-3.5 text-xs ${
            feedbackNotice.type === "success"
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
              : feedbackNotice.type === "error"
              ? "border-red-500/30 bg-red-500/10 text-red-300"
              : "border-amber-500/30 bg-amber-500/10 text-amber-300"
          }`}
        >
          <div className="flex items-start gap-2.5">
            {feedbackNotice.type === "success" ? (
              <CheckCircle2 size={16} className="shrink-0 text-emerald-400 mt-0.5" />
            ) : feedbackNotice.type === "error" ? (
              <XCircle size={16} className="shrink-0 text-red-400 mt-0.5" />
            ) : (
              <AlertTriangle size={16} className="shrink-0 text-amber-400 mt-0.5" />
            )}
            <div>
              <div className="font-semibold">{feedbackNotice.message}</div>
              {feedbackNotice.detail && (
                <div className="mt-1 font-mono text-[10px] opacity-80">
                  Detalle técnico: {feedbackNotice.detail}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setFeedbackNotice(null)}
            className="text-xs opacity-70 hover:opacity-100"
          >
            ✕
          </button>
        </div>
      )}

      {/* Lista de tarjetas */}
      {providers.length === 0 ? (
        <div
          data-testid="llm-providers-empty"
          className="rounded-2xl border border-dashed border-[#2a2a2a] bg-[#141414] p-8 text-center"
        >
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-[#2a2a2a] bg-[#1a1a1a] text-[#8a8a8a] mb-3">
            <Cpu size={22} />
          </div>
          <h3 className="font-display text-sm font-semibold text-[#ededed]">
            No hay proveedores LLM configurados
          </h3>
          <p className="mt-1 text-xs text-[#8a8a8a] max-w-md mx-auto leading-relaxed">
            Configure DeepSeek, Anthropic / Claude, Qwen / Alibaba, MiniMax o un endpoint personalizado para activar el razonamiento cinematográfico de VEYRA.
          </p>
          <button
            type="button"
            data-testid="llm-empty-add-btn"
            onClick={onOpenCreate}
            className="v-btn-primary v-btn-sm mt-4 inline-flex items-center gap-1.5"
          >
            <Plus size={14} />
            <span>Configurar primer proveedor</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {providers.map((prov) => {
            const statusMeta = STATUS_CONFIG[prov.status] || STATUS_CONFIG.not_configured;
            const StatusIcon = statusMeta.icon;
            const isTesting = testingProviderId === prov.id;
            const defaultModel = (prov.models || []).find((m) => m.is_default) || prov.models?.[0];

            return (
              <div
                key={prov.id}
                data-testid={`llm-provider-card-${prov.id}`}
                className="flex flex-col gap-4 rounded-2xl border border-[#262626] bg-[#141414] p-5 transition-all hover:border-[#333]"
              >
                {/* Cabecera de la tarjeta */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#222] pb-3.5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#2c2c2c] bg-[#1a1a1a] text-[#f5b544]">
                      <Cpu size={18} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-sm text-[#ededed]">{prov.label}</h3>
                        <span className="font-mono text-[10px] text-[#737373]">
                          ({prov.provider_type})
                        </span>
                      </div>
                      <div className="font-mono text-[11px] text-[#8a8a8a] truncate max-w-sm">
                        {prov.base_url}
                      </div>
                    </div>
                  </div>

                  {/* Badge de estado */}
                  <div className="flex items-center gap-2">
                    <span
                      data-testid={`llm-status-badge-${prov.id}`}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${statusMeta.badgeClass}`}
                    >
                      <StatusIcon size={12} />
                      <span>{statusMeta.label}</span>
                    </span>
                  </div>
                </div>

                {/* Info de clave y modelos */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  {/* Bloque Credenciales */}
                  <div className="rounded-xl border border-[#222] bg-[#171717] p-3 flex flex-col justify-between">
                    <div>
                      <span className="text-[11px] text-[#8a8a8a] block mb-1">Clave de API:</span>
                      {prov.has_key ? (
                        <div className="flex items-center gap-2">
                          <Lock size={12} className="text-emerald-400" />
                          <span className="font-mono text-xs text-[#ededed]">
                            {prov.key_hint || "sk-••••••••"}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-[#737373] italic">Sin clave configurada</span>
                      )}
                    </div>
                    {prov.last_tested_at && (
                      <span className="text-[10px] text-[#666] mt-2 block font-mono">
                        Última prueba: {new Date(prov.last_tested_at).toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* Bloque Modelos */}
                  <div className="rounded-xl border border-[#222] bg-[#171717] p-3 flex flex-col justify-between">
                    <div>
                      <span className="text-[11px] text-[#8a8a8a] block mb-1">
                        Modelos ({prov.models?.length || 0}):
                      </span>
                      {defaultModel ? (
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-xs text-[#f5b544]">
                            {defaultModel.label}
                          </span>
                          <span className="font-mono text-[10px] text-[#8a8a8a]">
                            ({defaultModel.id})
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-[#737373] italic">Ninguno configurado</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(defaultModel?.capabilities || []).map((cap) => (
                        <span
                          key={cap}
                          className="rounded bg-[#202020] px-1.5 py-0.5 text-[9px] text-[#aaa]"
                        >
                          {cap}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Error previo si aplica */}
                {prov.status === "error" && prov.last_error && (
                  <div
                    data-testid={`llm-card-error-${prov.id}`}
                    className="rounded-xl border border-red-500/20 bg-red-500/5 p-2.5 text-[11px] text-red-300 font-mono"
                  >
                    Detalle del error: {prov.last_error}
                  </div>
                )}

                {/* Barra de Acciones */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[#222] pt-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Botón Probar Credenciales (Metadata / Zero-Cost) */}
                    <button
                      type="button"
                      data-testid={`llm-test-credentials-btn-${prov.id}`}
                      disabled={!prov.has_key || isTesting}
                      onClick={() => handleRunCredentialsTest(prov)}
                      className="v-btn-secondary v-btn-xs inline-flex items-center gap-1.5"
                      title="Consulta metadatos de modelos sin ejecutar inferencia ni costes"
                    >
                      <CheckCircle2 size={12} />
                      <span>{isTesting ? "Verificando…" : "Probar Credenciales"}</span>
                    </button>

                    {/* Botón Probar Inferencia (Ping Sintético con Modal) */}
                    <button
                      type="button"
                      data-testid={`llm-test-inference-btn-${prov.id}`}
                      disabled={!prov.has_key || isTesting}
                      onClick={() => setInferenceModalTarget(prov)}
                      className="v-btn-ghost v-btn-xs inline-flex items-center gap-1.5 text-amber-400/90 hover:text-amber-300"
                      title="Requiere confirmación expresa de costes"
                    >
                      <Sparkles size={12} />
                      <span>Probar Inferencia</span>
                    </button>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      data-testid={`llm-edit-btn-${prov.id}`}
                      onClick={() => onOpenEdit(prov)}
                      className="rounded p-1.5 text-[#a3a3a3] hover:bg-[#222] hover:text-[#ededed]"
                      title="Editar configuración y modelos"
                    >
                      <Edit2 size={13} />
                    </button>
                    <button
                      type="button"
                      data-testid={`llm-delete-btn-${prov.id}`}
                      onClick={() => setDeleteTarget(prov)}
                      className="rounded p-1.5 text-[#888] hover:bg-red-500/10 hover:text-red-400"
                      title="Eliminar conexión"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de confirmación para prueba de inferencia */}
      <LLMTestConfirmModal
        open={Boolean(inferenceModalTarget)}
        provider={inferenceModalTarget}
        model={
          inferenceModalTarget?.models?.find((m) => m.is_default) ||
          inferenceModalTarget?.models?.[0]
        }
        loading={testingProviderId === inferenceModalTarget?.id}
        onClose={() => setInferenceModalTarget(null)}
        onConfirm={handleConfirmInferenceTest}
      />

      {/* Diálogo de confirmación para eliminar */}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={deleteTarget?.label || "Eliminar conexión"}
        body="¿Está seguro de que desea eliminar este proveedor LLM? Esta acción es irreversible."
        onConfirm={() => {
          if (deleteTarget) {
            onDelete(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
};

export default LLMProvidersList;
